import React, { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { Building2, Users, Search, Trash2, Plus, Briefcase, Camera, Film } from "lucide-react";
import { toast } from "sonner";

export default function LeadDiscovery() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [filter, setFilter] = useState("all"); // all | studio | director | producer | cameraman

  useEffect(() => {
    loadLeads();
  }, []);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/leads/list");
      setLeads(res.data.leads || []);
    } catch (err) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const runDiscovery = async () => {
    setDiscovering(true);
    try {
      const res = await api.post("/api/leads/discover", {
        categories: ["studio", "director", "producer"],
        regions: ["USA", "Europe"],
        max_results_per_query: 10,
      });
      toast.success(`✅ Discovered ${res.data.discovered} leads!`);
      loadLeads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Discovery failed");
    } finally {
      setDiscovering(false);
    }
  };

  const addToCRM = async (lead) => {
    try {
      // Create CRM entry from lead
      await api.post("/api/crm/", {
        stage_name: lead.name,
        category: lead.type === "studio" ? "studio" : "talent",
        photos: lead.image_url ? [lead.image_url] : [],
        website: lead.website,
        notes: `Auto-discovered: ${lead.description || ""}`,
      });
      toast.success(`✅ ${lead.name} added to CRM`);
      
      // Remove from discovery list
      await api.delete(`/api/leads/${lead.lead_id}`);
      loadLeads();
    } catch (err) {
      toast.error("Failed to add to CRM");
    }
  };

  const deleteLead = async (leadId) => {
    if (!confirm("Remove this lead?")) return;
    try {
      await api.delete(`/api/leads/${leadId}`);
      toast.success("Lead removed");
      loadLeads();
    } catch (err) {
      toast.error("Failed to delete lead");
    }
  };

  // Group leads by parent company
  const groupedLeads = leads.reduce((acc, lead) => {
    const parentKey = lead.parent_id || lead.lead_id;
    if (!acc[parentKey]) {
      acc[parentKey] = { parent: null, children: [] };
    }
    if (lead.parent_id) {
      acc[parentKey].children.push(lead);
    } else {
      acc[parentKey].parent = lead;
    }
    return acc;
  }, {});

  const filteredGroups = Object.values(groupedLeads).filter((group) => {
    if (filter === "all") return true;
    const allLeads = [group.parent, ...group.children].filter(Boolean);
    return allLeads.some(lead => lead.type === filter);
  });

  return (
    <div className="min-h-screen" style={{ background: "var(--gio-bg)" }}>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              🎬 Lead Discovery
            </h1>
            <p className="text-zinc-400">Discover studios, directors, and crew from the industry</p>
          </div>
          <button
            onClick={runDiscovery}
            disabled={discovering}
            className="px-6 py-3 rounded-lg font-semibold transition flex items-center gap-2"
            style={{
              background: discovering ? "#71717a" : "var(--gio-primary)",
              color: "#000",
            }}>
            {discovering ? (
              <>
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                Discovering...
              </>
            ) : (
              <>
                <Search size={18} />
                Run Discovery
              </>
            )}
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6">
          {[
            { v: "all", l: "All", icon: Users },
            { v: "studio", l: "Studios", icon: Building2 },
            { v: "director", l: "Directors", icon: Film },
            { v: "producer", l: "Producers", icon: Briefcase },
            { v: "cameraman", l: "Cameramen", icon: Camera },
          ].map(({ v, l, icon: Icon }) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              className="px-4 py-2 rounded-lg font-semibold transition flex items-center gap-2"
              style={{
                background: filter === v ? "var(--gio-primary)" : "var(--gio-surface)",
                color: filter === v ? "#000" : "#fff",
                border: `1px solid ${filter === v ? "var(--gio-primary)" : "var(--gio-border)"}`,
              }}>
              <Icon size={16} />
              {l}
            </button>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="p-4 rounded-xl" style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)" }}>
            <div className="text-2xl font-bold text-white">{leads.length}</div>
            <div className="text-sm text-zinc-400">Total Leads</div>
          </div>
          <div className="p-4 rounded-xl" style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)" }}>
            <div className="text-2xl font-bold text-white">{leads.filter(l => l.type === "studio").length}</div>
            <div className="text-sm text-zinc-400">Studios</div>
          </div>
          <div className="p-4 rounded-xl" style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)" }}>
            <div className="text-2xl font-bold text-white">{leads.filter(l => l.type === "director").length}</div>
            <div className="text-sm text-zinc-400">Directors</div>
          </div>
          <div className="p-4 rounded-xl" style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)" }}>
            <div className="text-2xl font-bold text-white">{leads.filter(l => l.parent_id).length}</div>
            <div className="text-sm text-zinc-400">Sub-brands</div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <div className="text-zinc-400">Loading leads...</div>
          </div>
        )}

        {/* Empty State */}
        {!loading && leads.length === 0 && (
          <div className="text-center py-12 rounded-xl" style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)" }}>
            <Building2 size={48} className="mx-auto mb-4 text-zinc-600" />
            <h3 className="text-xl font-bold text-white mb-2">No leads discovered yet</h3>
            <p className="text-zinc-400 mb-4">Run discovery to find studios, directors, and crew</p>
            <button
              onClick={runDiscovery}
              className="px-6 py-3 rounded-lg font-semibold transition"
              style={{ background: "var(--gio-primary)", color: "#000" }}>
              <Search size={18} className="inline mr-2" />
              Start Discovery
            </button>
          </div>
        )}

        {/* Lead Groups */}
        {!loading && filteredGroups.length > 0 && (
          <div className="space-y-6">
            {filteredGroups.map((group) => (
              <div key={group.parent?.lead_id || group.children[0]?.lead_id}>
                {/* Parent Lead */}
                {group.parent && (
                  <LeadCard
                    lead={group.parent}
                    isParent={true}
                    onAddToCRM={addToCRM}
                    onDelete={deleteLead}
                  />
                )}
                
                {/* Child Leads (Sub-brands) */}
                {group.children.length > 0 && (
                  <div className="ml-8 mt-4 space-y-4 border-l-2" style={{ borderColor: "var(--gio-border)" }}>
                    {group.children.map((child) => (
                      <div key={child.lead_id} className="pl-6">
                        <LeadCard
                          lead={child}
                          isParent={false}
                          onAddToCRM={addToCRM}
                          onDelete={deleteLead}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LeadCard({ lead, isParent, onAddToCRM, onDelete }) {
  const typeIcons = {
    studio: Building2,
    director: Film,
    producer: Briefcase,
    cameraman: Camera,
  };
  const Icon = typeIcons[lead.type] || Users;

  return (
    <div
      className="p-4 rounded-xl flex gap-4"
      style={{
        background: isParent ? "var(--gio-surface)" : "rgba(255,255,255,0.02)",
        border: `1px solid ${isParent ? "var(--gio-border)" : "rgba(255,255,255,0.05)"}`,
      }}>
      {/* Image */}
      {lead.image_url ? (
        <img
          src={lead.image_url}
          alt={lead.name}
          className="w-24 h-24 rounded-lg object-cover"
        />
      ) : (
        <div
          className="w-24 h-24 rounded-lg flex items-center justify-center"
          style={{ background: "var(--gio-surface-2)" }}>
          <Icon size={32} className="text-zinc-600" />
        </div>
      )}

      {/* Info */}
      <div className="flex-1">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              {lead.name}
              {isParent && lead.is_parent && (
                <span className="text-xs px-2 py-0.5 rounded" style={{ background: "#a855f7", color: "#000" }}>
                  PARENT
                </span>
              )}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs px-2 py-0.5 rounded" style={{ background: "var(--gio-primary-soft)", color: "var(--gio-primary)" }}>
                <Icon size={12} className="inline mr-1" />
                {lead.type}
              </span>
              {lead.region && (
                <span className="text-xs text-zinc-400">{lead.region}</span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onAddToCRM(lead)}
              className="px-3 py-1 rounded text-sm font-semibold transition flex items-center gap-1"
              style={{ background: "#22c55e", color: "#000" }}>
              <Plus size={14} />
              Add to CRM
            </button>
            <button
              onClick={() => onDelete(lead.lead_id)}
              className="p-1 rounded text-red-400 hover:text-red-300 transition">
              <Trash2 size={16} />
            </button>
          </div>
        </div>

        {lead.description && (
          <p className="text-sm text-zinc-400 mb-2 line-clamp-2">{lead.description}</p>
        )}

        {lead.website && (
          <a
            href={lead.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-cyan-400 hover:text-cyan-300">
            {lead.website}
          </a>
        )}
      </div>
    </div>
  );
}
